import { DateTimefromYmd } from '#lib/helper/date.js';
import { goto } from '$app/navigation';
import { hasTextContent } from '#lib/html/input/richtextarea-front.js';

import { DateTime } from 'luxon';
import { derived, get, writable, type Readable } from 'svelte/store';
import { activityStore } from '#lib/stores/activity.js';
import { offerStore } from '#lib/stores/offer.js';
import {
	cancelBookingIntent,
	clearCacheAvails,
	widgetAvails,
	widgetBooking,
	widgetBookingExpiresAt,
	widgetBookingReset,
	widgetOffers,
	widgetPrices
} from './booking';
import {
	OfferEngineType,
	WidgetAuthMode,
	type CustomerPackage,
	type WidgetActivity,
	type WidgetContent,
	type WidgetOffer,
	type WidgetProposal
} from '#lib/types/generated/index.js';
import { mapWidgetActivityToActivity, mapWidgetOfferToOffer } from '#lib/widget/mapper.js';
import {
	filterWidgetContentByCustomerPackage,
	widgetCustomerPackageToBeUsed
} from '#lib/widget/packages/packages.js';
import {
	getDefaultWidgetContent,
	restoreDefaultWidgetContent,
	widgetConfig,
	widgetContent,
	widgetShowHeaderContactInfo
} from '../core';

export type WidgetStepType =
	| 'activity'
	| 'pax'
	| 'offer'
	// | 'offerCategory'
	| 'events'
	| 'hub'
	| 'price'
	| 'product'
	| 'customer'
	| 'payment'
	| 'confirmation'
	| 'thank-you';

const flow1: WidgetStepType[] = [
	'activity',
	'pax',
	'hub',
	// 'offerCategory',
	//'offer',
	'price',
	'product',
	'customer',
	'payment',
	'confirmation',
	'thank-you'
];

const flow2: WidgetStepType[] = [
	'activity',
	// 'offerCategory',
	'offer',
	'pax',
	'hub',
	'price',
	'product',
	'customer',
	'payment',
	'confirmation',
	'thank-you'
];

const flow3: WidgetStepType[] = [
	'activity',
	'offer',
	'pax',
	'hub',
	'price',
	'product',
	'customer',
	'payment',
	'confirmation',
	'thank-you'
];

const flowEvent: WidgetStepType[] = [
	'activity',
	'offer',
	'events',
	'pax',
	'price',
	'product',
	'customer',
	'payment',
	'confirmation',
	'thank-you'
];

let baseFlow: WidgetStepType[] = flow1;

export const widgetFlow = writable<WidgetStepType[]>(flow1);
export const widgetCurrentStep = writable<WidgetStepType>('activity');

export function widgetSetFlow(flow: 'flow1' | 'flow2' | 'flow3' | undefined): void {
	if (flow === 'flow1') {
		baseFlow = flow1;
		widgetFlow.set(flow1);
	}
	if (flow === 'flow2') {
		baseFlow = flow2;
		widgetFlow.set(flow2);
	}
	if (flow === 'flow3') {
		baseFlow = flow3;
		widgetFlow.set(flow3);
	}
}

export function activateEventFlow(isEvent: boolean): void {
	if (isEvent) {
		widgetFlow.set(flowEvent);
	} else {
		widgetFlow.set(baseFlow);
	}
}

function firstStep(): WidgetStepType {
	return get(widgetFlow)[0];
}

function nextStep(): WidgetStepType {
	const flow = get(widgetFlow);
	const currentIndex = flow.indexOf(get(widgetCurrentStep));

	if (currentIndex === -1) {
		return flow[0];
	}

	return flow[currentIndex + 1];
}

function previousStep(): WidgetStepType {
	const flow = get(widgetFlow);
	const currentIndex = flow.indexOf(get(widgetCurrentStep));

	if (currentIndex === -1) {
		return flow[0];
	}

	return flow[currentIndex - 1];
}

export function clearNextSteps(step: undefined | WidgetStepType = undefined): void {
	const flow = get(widgetFlow);
	const targetStep = step ?? get(widgetCurrentStep);
	const currentIndex = flow.indexOf(targetStep);

	if (currentIndex === -1) {
		return;
	}

	for (let i = currentIndex + 1; i < flow.length; i++) {
		clearStep(flow[i]);
	}
}

function clearStep(step: WidgetStepType): void {
	switch (step) {
		case 'activity':
			delete get(widgetBooking).activity;
			freeBooking();
			break;
		case 'pax':
			delete get(widgetBooking).pax;
			freeBooking();
			break;
		case 'offer':
			delete get(widgetBooking).offer;
			delete get(widgetBooking).collectionId;
			freeBooking();
			break;
		case 'events':
		case 'hub':
			freeBooking();
			get(widgetBooking).sessions = [];
			break;
		case 'price':
			freeBooking();
			widgetPrices.set(undefined);
			get(widgetBooking).prices = {};
			get(widgetBooking).packageUsages = [];
			break;
		case 'product':
			get(widgetBooking).products = {};
		case 'payment':
		case 'customer':
		case 'confirmation':
		case 'thank-you':
			break;
	}
}

export function clearFromStep(step: WidgetStepType): void {
	clearStep(step);
	clearNextSteps(step);
}

export function stepAlreadyDone(step: WidgetStepType): boolean {
	const currentIndex = get(widgetFlow).indexOf(step);
	if (currentIndex === -1) return false;
	return currentIndex < get(widgetFlow).indexOf(get(widgetCurrentStep));
}

function freeBooking(): void {
	clearCacheAvails();
	void cancelBookingIntent(get(widgetBooking).id);
	const b = get(widgetBooking);
	b.id = '';
	widgetBooking.set(b);
}

export function hasUniqueActivityChoice(): WidgetActivity | undefined {
	const activities = get(widgetContent).activities;
	return activities.length === 1 ? activities[0] : undefined;
}

export function hasUniqueOfferInActivityChoice(): WidgetOffer | undefined {
	const activity = get(widgetBooking).activity;
	if (!activity) return undefined;

	const offers = Object.values(get(widgetContent).offers).filter((o) =>
		o.activityIds.includes(activity.id)
	);

	return offers.length === 1 ? offers[0] : undefined;
}

export const hasUniqueOfferChoice: Readable<(checkDate?: boolean) => WidgetOffer | undefined> =
	derived(
		[widgetBooking, widgetContent, widgetAvails],
		([$widgetBooking, $widgetContent, $widgetAvails]) =>
			(checkDate?: boolean): WidgetOffer | undefined => {
				const activityId = $widgetBooking.activity?.id;
				let offers = Object.values($widgetContent.offers).filter(
					(o) => activityId === undefined || o.activityIds.includes(activityId)
				);

				if (checkDate) {
					const targetDate = $widgetBooking.date?.toISODate() ?? undefined;
					if (targetDate && Object.keys($widgetAvails.offers).length > 0) {
						offers = offers.filter((offer) => {
							return (
								targetDate in $widgetAvails.offers &&
								offer.id in $widgetAvails.offers[targetDate]
							);
						});
					}
				}

				if (offers.length === 1) {
					return offers[0];
				}
				return undefined;
			}
	);

export let hasUniqueDateChoice = derived(widgetBooking, ($widgetBooking): DateTime | undefined => {
	const offer = $widgetBooking.offer;
	if (offer && offer.from && offer.from === offer.to) {
		return DateTimefromYmd(offer.from)!;
	}
	return undefined;
});

export function hasUniquePaxChoice(): number | undefined {
	const offer = get(widgetBooking).offer;
	if (offer && offer.minPax === offer.maxPax) {
		return offer.minPax;
	}

	const collectionId = get(widgetBooking).collectionId;
	if (collectionId) {
		const offers = get(widgetOffers).filter((o) => o.collectionId === collectionId);
		if (
			offers.length > 0 &&
			offers.every((o) => o.minPax === o.maxPax && o.minPax === offers[0].minPax)
		) {
			return offers[0].minPax;
		}
	}

	const activity = get(widgetBooking).activity;

	if (activity && activity.minPax === activity.maxPax) {
		return activity.minPax;
	}

	return undefined;
}

export let hasUniqueSession = derived(
	[widgetAvails, widgetBooking],
	([$widgetAvails, $widgetBooking]): WidgetProposal | undefined => {
		const dateKey = $widgetBooking.date?.toISODate() ?? '';
		if (!(dateKey in $widgetAvails.offers)) return undefined;
		const date = $widgetAvails.offers[dateKey];
		const offerId = $widgetBooking.offer?.id ?? '';
		if (!(offerId in date)) return undefined;
		const proposals = date[offerId];

		if (proposals.length === 1) {
			return proposals[0];
		}
		return undefined;
	}
);

export function hasWelcomePage(): boolean {
	const config = get(widgetConfig);
	const content = get(widgetContent);

	return (
		hasTextContent(config.customContent.welcome) ||
		content.giftcards.length > 0 ||
		content.packages.length > 0 ||
		config.memberArea.authMode !== WidgetAuthMode.None ||
		content.activities.length === 0
	);
}

export function isStepEligible(step: WidgetStepType): boolean {
	const content = get(widgetContent);
	const config = get(widgetConfig);
	const booking = get(widgetBooking);

	switch (step) {
		case 'activity':
			return content.activities.length > 1;
		case 'offer': {
			if (!booking.activity) return false;
			const offers = get(widgetOffers);
			if (offers.length <= 1) return false;

			const isActivityAllEvents = offers.every((o) => o.engine === OfferEngineType.Event);
			if (isActivityAllEvents) {
				const activeCollections = Object.values(content.offerCollections).filter(
					(collection) => offers.some((o) => o.collectionId === collection.id)
				);
				return Boolean(config.showCollectionInGrid && activeCollections.length > 1);
			}
			return get(hasUniqueOfferChoice)(true) === undefined;
		}
		case 'events':
		case 'hub':
			return true;
		case 'pax':
			return hasUniquePaxChoice() === undefined;
		case 'price':
			return true;
		case 'product':
			return Object.values(content.products).length > 0;
		default:
			return true;
	}
}

export function getPreviousEligibleStep(fromStep?: WidgetStepType): WidgetStepType | null {
	const flow = get(widgetFlow);
	const currentIndex = flow.indexOf(fromStep ?? get(widgetCurrentStep));

	if (currentIndex <= 0) return null;

	for (let i = currentIndex - 1; i >= 0; i--) {
		const step = flow[i];
		if (isStepEligible(step)) {
			return step;
		}
	}

	return null;
}

export function gotoStep(step: WidgetStepType, replace: boolean = false): void {
	clearFromStep(step);
	goto(`/widget/${get(widgetConfig).hash}/book/${step}`, { replace });
}

export function gotoPreviousStep(replace: boolean = true): void {
	const prev = getPreviousEligibleStep();
	if (prev) {
		gotoStep(prev, replace);
	} else {
		goto('./' + previousStep(), { replace });
	}
}

export function gotoNextStep(replace: boolean = false): void {
	goto('./' + nextStep(), { replace, refreshAll: true });
}

export function gotoFirstStep(replace: boolean = false, customContent?: WidgetContent): void {
	if (customContent) {
		widgetContent.set(customContent);
		activityStore.set(customContent.activities.map(mapWidgetActivityToActivity));
		offerStore.set(Object.values(customContent.offers).map(mapWidgetOfferToOffer));
	} else {
		restoreDefaultWidgetContent();
		widgetCustomerPackageToBeUsed.set(undefined);
		const content = get(widgetContent);
		activityStore.set(content.activities.map(mapWidgetActivityToActivity));
		offerStore.set(Object.values(content.offers).map(mapWidgetOfferToOffer));
	}

	goto(`/widget/${get(widgetConfig).hash}/book/${firstStep()}`, { replace });
}

export function useCustomerPackageCredits(customerPackage: CustomerPackage): void {
	widgetBookingExpiresAt.set(undefined);
	widgetBookingReset();
	clearCacheAvails();
	widgetCustomerPackageToBeUsed.set(customerPackage);
	const filteredContent = filterWidgetContentByCustomerPackage(
		getDefaultWidgetContent(),
		customerPackage
	);
	gotoFirstStep(false, filteredContent);
}

export function resetWidget(): void {
	void cancelBookingIntent(get(widgetBooking).id);
	let url = '/widget/' + get(widgetConfig).hash;
	if (get(widgetShowHeaderContactInfo)) {
		url += '/google-reserve';
	}
	window.location.href = url;
}
